import React, { useEffect, useState } from "react";
import { Button, Modal, ModalBody } from "reactstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const PaymentBar = ({ order }) => {

  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [hasShownPopup, setHasShownPopup] = useState(false);
  const [hideBar, setHideBar] = useState(false);
  const [storedPaidOrder, setStoredPaidOrder] = useState(null);

  const navigate = useNavigate();
  const [params] = useSearchParams();

  const tenant =
    params.get("tenant") ||
    localStorage.getItem("tenantId");

  const tableNo =
    params.get("table") ||
    localStorage.getItem("tableNo");

  /* ================= LOAD LOCALSTORAGE ================= */

  useEffect(() => {
    const paid = localStorage.getItem("paidOrder");
    if (paid) {
      setStoredPaidOrder(paid);
    }
  }, []);

  /* ================= PAYMENT DETECTION ================= */

  useEffect(() => {
    if (
      order?.paymentStatus === "Paid" &&
      (order?.orderId || order?._id) &&
      !hasShownPopup &&
      storedPaidOrder !== (order.orderId || order._id)
    ) {
      setPaymentSuccess(true);
      setHasShownPopup(true);
    }
  }, [
    order?.paymentStatus,
    order?.orderId,
    order?._id,
    storedPaidOrder
  ]);

  if (!order) return null;

  const orderId = order.orderId || order._id;
  const total = order.finalAmount || order.totalAmount;

  if (storedPaidOrder && storedPaidOrder === orderId) {
    return null;
  }

  if (hideBar) return null;

  /* ================= PAY ================= */

  const handlePayAtReception = () => {
    alert("Please proceed to reception for payment.");
  };

  /* ================= PDF GENERATOR ================= */

const handleDownloadInvoice = () => {
  if (!order) return;

  const doc = new jsPDF();

  // ================= SAFE HEADER VALUES =================
  const orderNo = order.orderNo || order.orderId || order._id || "N/A";
  const table = order.tableNo || tableNo || "N/A";
  const payment = order.paymentStatus || "Pending";

  const date = order.createdAt
    ? new Date(order.createdAt).toLocaleString()
    : new Date().toLocaleString();

  // ================= SAFE ITEMS =================
  const items = (order.items || []).map(item => {
    const qty = item.quantity || item.qty || 1;
    const price = item.price || 0;
    const total = qty * price;

    return {
      name: item.name || "Item",
      qty,
      price,
      total
    };
  });

  // ================= TOTAL CALCULATION =================
  const subtotal =
    order.totalAmount ??
    items.reduce((sum, i) => sum + i.total, 0);

  const discount = order.discountAmount ?? 0;

  const finalAmount =
    order.finalAmount ??
    subtotal - discount;

  // ================= HEADER =================
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("INVOICE", 14, 20);

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");

  doc.text(`Order No: ${orderNo}`, 14, 30);
  doc.text(`Table: ${table}`, 14, 36);
  doc.text(`Date: ${date}`, 14, 42);

  doc.text(`Payment: ${payment}`, 140, 30);

  doc.line(14, 48, 196, 48);

  // ================= TABLE =================
  const tableData = items.map(i => [
    i.name,
    i.qty,
    `Rs. ${i.price}`,
    `Rs. ${i.total}`
  ]);

  autoTable(doc, {
    startY: 55,
    head: [["Item", "Qty", "Price", "Total"]],
    body: tableData,
    theme: "grid",
    styles: { fontSize: 10 },
    headStyles: {
      fillColor: [40, 167, 69],
      textColor: 255
    },
    columnStyles: {
      1: { halign: "center" },
      2: { halign: "right" },
      3: { halign: "right" }
    }
  });

  let finalY = doc.lastAutoTable.finalY + 10;
  const rightX = 196;

  // ================= SUMMARY =================
  doc.setFontSize(11);

  doc.text(`Subtotal: Rs. ${subtotal}`, rightX, finalY, {
    align: "right"
  });

  finalY += 6;

  if (discount > 0) {
    doc.text(`Discount: -Rs. ${discount}`, rightX, finalY, {
      align: "right"
    });
    finalY += 6;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);

  doc.text(`Total Payable: Rs. ${finalAmount}`, rightX, finalY, {
    align: "right"
  });

  finalY += 10;

  // ================= FOOTER =================
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");

  doc.text("Thank you for visiting!", 14, finalY);
  doc.text("Powered by Nexify RMS", 14, finalY + 6);

  // ================= SAVE =================
  doc.save(`Invoice_${orderNo}.pdf`);
};

  return (
    <>
      {/* PAYMENT BAR */}
      <div style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        background: "#fff",
        padding: 16,
        boxShadow: "0 -4px 12px rgba(0,0,0,0.1)",
        zIndex: 1000
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 10
        }}>
          <strong>Total Amount</strong>
          <strong style={{ fontSize: 18 }}>₹{total}</strong>
        </div>

        <Button
          color="primary"
          block
          onClick={handlePayAtReception}
        >
          Pay at Reception
        </Button>

        {/* 🔽 DOWNLOAD LINK */}
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <span
            onClick={handleDownloadInvoice}
            style={{
              color: "#007bff",
              textDecoration: "none",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 500
            }}
          >
            Download Invoice
          </span>
        </div>
      </div>

      {/* SUCCESS MODAL */}
      <Modal isOpen={paymentSuccess} centered>
        <ModalBody className="text-center">
          <h4 className="mb-2">Thank You! 🙏</h4>
          <p className="text-muted">
            Your payment has been received successfully.
          </p>

          <Button
            color="success"
            block
            onClick={() => {
              setPaymentSuccess(false);
              setHideBar(true);

              const id = order.orderId || order._id;

              localStorage.setItem("paidOrder", id);
              setStoredPaidOrder(id);

              navigate(
                `/guest-review?tenant=${tenant}&table=${tableNo}&orderId=${id}`
              );
            }}
          >
            Share Feedback
          </Button>
        </ModalBody>
      </Modal>
    </>
  );
};

export default PaymentBar;
